import * as React from "react";

import {RequestInfo} from "@khan/shared-core";

import {toGqlContextRecord} from "./to-gql-context-record.ts";
import type {GqlContext, GqlContextRecord} from "./types.ts";

/**
 * Get Wonder Blocks-compatible context from default and partial context.
 *
 * This takes the current context from the GqlRouterContext and combines it
 * with the provided partial context to create a full context for use with
 * Wonder Blocks Data GraphQL operations.
 *
 * @returns A function that takes a partial context and returns a full
 * Wonder Blocks-compatible context.
 */
export const useGqlContext = (): ((
    context?: Partial<GqlContext>,
) => GqlContextRecord) => {
    // Memoize the combined context based off these values.
    const combineGqlContexts = React.useCallback(
        (
            {
                // NOTE(somewhatabstract, FEI-4906): We do not default the
                // curriculum value here. This gives us a better cache hit
                // rates for operations that don't need it. Operations that
                // require the curriculum context value should explicitly
                // provide it.
                curriculum,

                fastlyCaching,
                targetLocale = RequestInfo.kaLocale,
            }: Partial<GqlContext> = Object.freeze({}),
        ) =>
            toGqlContextRecord({
                targetLocale,
                curriculum,
                fastlyCaching,
            }),
        [],
    );

    // Return our memoized version.
    return combineGqlContexts;
};
